// Admin API Service for Admin Dashboard
const BASE_URL = process.env.REACT_APP_API_URL;

// Auth token helper (using existing auth system)
const getAuthHeaders = () => {
  const token = localStorage.getItem("notaToken"); // Use the same token as the auth system
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const adminApiService = {
  // Consultations API
  consultations: {
    // Get all consultation leads with filtering and pagination
    getLeads: async (filters = {}) => {
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.status && filters.status !== "all")
        params.append("status", filters.status);
      if (filters.serviceType && filters.serviceType !== "all")
        params.append("serviceType", filters.serviceType);
      if (filters.consultationType && filters.consultationType !== "all")
        params.append("consultationType", filters.consultationType);

      const url = `${BASE_URL}/api/nota/consultation/leads?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch consultation leads" }));
        throw new Error(
          errorData.message || "Failed to fetch consultation leads",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get single consultation lead
    getLead: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/consultation/lead/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch consultation lead" }));
        throw new Error(
          errorData.message || "Failed to fetch consultation lead",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Update consultation lead status
    updateStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/consultation/lead/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update consultation status" }));
        throw new Error(
          errorData.message || "Failed to update consultation status",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Bulk delete consultation leads
    bulkDelete: async (ids) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/consultation/leads/bulk-delete`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ ids }),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to delete consultation leads" }));
        throw new Error(
          errorData.message || "Failed to delete consultation leads",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Export consultation leads to CSV
    exportToCSV: async (filters = {}) => {
      const params = new URLSearchParams();

      if (filters.status && filters.status !== "all")
        params.append("status", filters.status);
      if (filters.serviceType && filters.serviceType !== "all")
        params.append("serviceType", filters.serviceType);
      if (filters.consultationType && filters.consultationType !== "all")
        params.append("consultationType", filters.consultationType);

      const url = `${BASE_URL}/api/nota/consultation/leads/export?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to export consultation leads" }));
        throw new Error(
          errorData.message || "Failed to export consultation leads",
        );
      }

      // Get the CSV content as text and create blob
      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      return blob;
    },

    // Get consultation statistics
    getStats: async () => {
      const response = await fetch(`${BASE_URL}/api/nota/consultation/stats`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch consultation stats" }));
        throw new Error(
          errorData.message || "Failed to fetch consultation stats",
        );
      }

      const data = await response.json();
      return data.data;
    },
  },

  // Study Pipeline API
  studyPipeline: {
    // Get all study abroad students with filtering and pagination
    getStudents: async (filters = {}) => {
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.pipelineStatus && filters.pipelineStatus !== "all")
        params.append("pipelineStatus", filters.pipelineStatus);
      if (filters.paymentStatus && filters.paymentStatus !== "all")
        params.append("paymentStatus", filters.paymentStatus);
      if (filters.search) params.append("search", filters.search);

      const url = `${BASE_URL}/api/nota/admin/study/students?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch study students" }));
        throw new Error(errorData.message || "Failed to fetch study students");
      }

      const data = await response.json();
      return data.data;
    },

    // Get study pipeline statistics
    getStats: async () => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/study/stats`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch study pipeline stats" }));
        throw new Error(
          errorData.message || "Failed to fetch study pipeline stats",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get single student details
    getStudent: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch student details" }));
        throw new Error(errorData.message || "Failed to fetch student details");
      }

      const data = await response.json();
      return data.data;
    },

    // Update student pipeline status
    updateStudentStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update student status" }));
        throw new Error(errorData.message || "Failed to update student status");
      }

      const data = await response.json();
      return data.data;
    },

    // Get student documents
    getStudentDocuments: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/documents`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch student documents" }));
        throw new Error(
          errorData.message || "Failed to fetch student documents",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get student personal details
    getStudentPersonalDetails: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/personal-details`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch student personal details",
          }));
        throw new Error(
          errorData.message || "Failed to fetch student personal details",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get student academic history
    getStudentAcademicHistory: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/academic-history`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch student academic history",
          }));
        throw new Error(
          errorData.message || "Failed to fetch student academic history",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get student programme preferences
    getStudentProgrammePreferences: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/programme-preferences`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch student programme preferences",
          }));
        throw new Error(
          errorData.message || "Failed to fetch student programme preferences",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Update document status
    updateDocumentStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/document/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update document status" }));
        throw new Error(
          errorData.message || "Failed to update document status",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get student SOP
    getStudentSOP: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/sop`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch student SOP" }));
        throw new Error(errorData.message || "Failed to fetch student SOP");
      }

      const data = await response.json();
      return data.data;
    },

    // Update SOP status
    updateSOPStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/sop/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update SOP status" }));
        throw new Error(errorData.message || "Failed to update SOP status");
      }

      const data = await response.json();
      return data.data;
    },

    // Add admin note to student
    addAdminNote: async (id, note) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/study/student/${id}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ note }),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to add admin note" }));
        throw new Error(errorData.message || "Failed to add admin note");
      }

      const data = await response.json();
      return data.data;
    },

    // Export students to CSV
    exportToCSV: async (filters = {}) => {
      const params = new URLSearchParams();

      if (filters.pipelineStatus && filters.pipelineStatus !== "all")
        params.append("pipelineStatus", filters.pipelineStatus);
      if (filters.paymentStatus && filters.paymentStatus !== "all")
        params.append("paymentStatus", filters.paymentStatus);

      const url = `${BASE_URL}/api/nota/admin/study/students/export?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to export study students" }));
        throw new Error(errorData.message || "Failed to export study students");
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      return blob;
    },
  },

  // Work Pipeline API
  workPipeline: {
    // Get all work students
    getStudents: async (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/students?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch work students" }));
        throw new Error(errorData.message || "Failed to fetch work students");
      }

      const data = await response.json();
      return data.data;
    },

    // Get work pipeline statistics
    getStats: async () => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/work/stats`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch work stats" }));
        throw new Error(errorData.message || "Failed to fetch work stats");
      }

      const data = await response.json();
      return data.data;
    },

    // Get single work student
    getStudent: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch work student" }));
        throw new Error(errorData.message || "Failed to fetch work student");
      }

      const data = await response.json();
      return data.data;
    },

    // Update work student status
    updateStudentStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update work student status" }));
        throw new Error(
          errorData.message || "Failed to update work student status",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get work student documents
    getStudentDocuments: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/documents`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch work student documents" }));
        throw new Error(
          errorData.message || "Failed to fetch work student documents",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get work student personal details
    getStudentPersonalDetails: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/personal-details`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch work student personal details",
          }));
        throw new Error(
          errorData.message || "Failed to fetch work student personal details",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get work student work history
    getStudentWorkHistory: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/work-history`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch work student work history",
          }));
        throw new Error(
          errorData.message || "Failed to fetch work student work history",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get work student role preferences
    getStudentRolePreferences: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/role-preferences`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch work student role preferences",
          }));
        throw new Error(
          errorData.message || "Failed to fetch work student role preferences",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Get work student interview details
    getStudentInterviewDetails: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/interview-details`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({
            message: "Failed to fetch work student interview details",
          }));
        throw new Error(
          errorData.message || "Failed to fetch work student interview details",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Update document status
    updateDocumentStatus: async (id, updates) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/document/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update work document status" }));
        throw new Error(
          errorData.message || "Failed to update work document status",
        );
      }

      const data = await response.json();
      return data.data;
    },

    // Add admin note to work student
    addAdminNote: async (id, note) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/work/student/${id}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ note }),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to add work student note" }));
        throw new Error(errorData.message || "Failed to add work student note");
      }

      const data = await response.json();
      return data.data;
    },

    // Export work students to CSV
    exportStudents: async (params = {}) => {
      const queryParams = new URLSearchParams(params);

      const url = `${BASE_URL}/api/nota/admin/work/students/export?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to export work students" }));
        throw new Error(errorData.message || "Failed to export work students");
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      return blob;
    },
  },

  // Payments Admin API
  payments: {
    // Get all payments with filtering and pagination
    getPayments: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/payments?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch payments" }));
        throw new Error(errorData.message || "Failed to fetch payments");
      }

      return response.json();
    },

    // Get payment statistics
    getPaymentStats: async (dateRange = "30") => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/payments/stats?dateRange=${dateRange}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch payment stats" }));
        throw new Error(errorData.message || "Failed to fetch payment stats");
      }

      return response.json();
    },

    // Mark student as paid
    markAsPaid: async (email, program) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/payments/mark-paid`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, program }),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to mark student as paid" }));
        throw new Error(errorData.message || "Failed to mark student as paid");
      }

      return response.json();
    },

    // Bulk mark students as paid
    bulkMarkAsPaid: async (students) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/payments/bulk-mark-paid`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ students }),
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to bulk mark students as paid" }));
        throw new Error(
          errorData.message || "Failed to bulk mark students as paid",
        );
      }

      return response.json();
    },

    // Export payments to CSV
    exportPayments: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/payments/export?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to export payments" }));
        throw new Error(errorData.message || "Failed to export payments");
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      return blob;
    },
  },

  // Dashboard API
  dashboard: {
    // Get dashboard statistics
    getStats: async () => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/dashboard/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch dashboard stats" }));
        throw new Error(errorData.message || "Failed to fetch dashboard stats");
      }

      const result = await response.json();
      return result.data;
    },

    // Get recent activity
    getActivity: async () => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/dashboard/activity`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch recent activity" }));
        throw new Error(errorData.message || "Failed to fetch recent activity");
      }

      const result = await response.json();
      return result.data;
    },
  },

  // Roles Admin API
  roles: {
    // Get all roles with filtering and pagination
    getRoles: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/roles?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch roles" }));
        throw new Error(errorData.message || "Failed to fetch roles");
      }

      return response.json();
    },

    // Get role statistics
    getRoleStats: async () => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch role stats" }));
        throw new Error(errorData.message || "Failed to fetch role stats");
      }

      return response.json();
    },

    // Get single role by ID
    getRoleById: async (id) => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to fetch role" }));
        throw new Error(errorData.message || "Failed to fetch role");
      }

      return response.json();
    },

    // Create new role
    createRole: async (roleData) => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to create role" }));
        throw new Error(errorData.message || "Failed to create role");
      }

      return response.json();
    },

    // Update role
    updateRole: async (id, roleData) => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to update role" }));
        throw new Error(errorData.message || "Failed to update role");
      }

      return response.json();
    },

    // Delete role
    deleteRole: async (id) => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to delete role" }));
        throw new Error(errorData.message || "Failed to delete role");
      }

      return response.json();
    },

    // Toggle role status
    toggleRoleStatus: async (id) => {
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/roles/${id}/toggle`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to toggle role status" }));
        throw new Error(errorData.message || "Failed to toggle role status");
      }

      return response.json();
    },

    // Bulk delete roles
    bulkDeleteRoles: async (roleIds) => {
      const response = await fetch(`${BASE_URL}/api/nota/admin/roles/bulk`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ roleIds }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to bulk delete roles" }));
        throw new Error(errorData.message || "Failed to bulk delete roles");
      }

      return response.json();
    },

    // Export roles to CSV
    exportRoles: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(
        `${BASE_URL}/api/nota/admin/roles/export?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("notaToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to export roles" }));
        throw new Error(errorData.message || "Failed to export roles");
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      return blob;
    },
  },
};

export default adminApiService;
